import { AutoColumn } from 'components/Column';
import { observer } from 'mobx-react';
import { TYPE } from 'theme';
import React from 'react';
import { isMobile } from 'react-device-detect';
import { ReactComponent as GiftIcon } from 'assets/images/lucky-draw/gift.svg';
import { ReactComponent as BoxDoubtIcon } from 'assets/images/lucky-draw/box_doubt.svg';
import { ReactComponent as PointsIcon } from 'assets/images/lucky-draw/points.svg';
import { ReactComponent as CheckBoxIcon } from 'assets/images/lucky-draw/check.svg';
import Row, { RowBetween } from 'components/Row';
import styled from 'styled-components';
import { Button } from '@mui/material';
import { ArrowIcon } from 'components/Icons';
import luckyDrawStore from 'stores/luckyDrawStore';
import { getBalanceNumber, getFullDisplayBalance } from 'utils';
import BigNumber from 'bignumber.js';
import TooltipInfo from 'components/TooltipInfo';
import ErrorIcon from '@mui/icons-material/Error';

const CheckCircle = styled.div<{ active?: boolean }>`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${({ active }) => (active ? '#5FD15C' : '#585858')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
        width: 8px;
        height: 8px;
    }
`;

const BindInviteWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    padding: 7px 15px;
    background: transparent;
    border: 1px solid rgba(88, 88, 88, 1);
    border-radius: 32px;
    transition: all 0.3s ease;
    flex-shrink: 0;
    justify-content: center;
    &:hover {
        opacity: 0.8;
    }
`;

const CheckIcon = () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.5 2L3 5.5L1.5 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const BasicInfoContainer = styled(AutoColumn)`
    gap: 40px;
    background-color: #000000cc;
    padding: 40px;
    border-radius: 24px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    @media (max-width: 768px) {
        padding: 20px;
        border-radius: 16px;
        gap: 30px;
    }

    @media (max-width: 480px) {
        padding: 16px;
        border-radius: 12px;
        gap: 20px;
    }
`;

const InfoCardsContainer = styled.div`
    display: flex;
    gap: 20px;
    flex-direction: row;
    justify-content: space-between;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 15px;
    }

    @media (max-width: 480px) {
        gap: 12px;
    }
`;

const InfoCard = styled.div`
    display: flex;
    gap: 20px;

    @media (max-width: 480px) {
        gap: 12px;
    }
`;

const InfoIcon = styled.div`
    width: 60px;
    height: 60px;
    background-color: #f7931a1a;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    @media (max-width: 480px) {
        width: 48px;
        height: 48px;
        border-radius: 10px;
    }
`;

const DetailItem = styled(TYPE.desc)<{ $showBorder?: boolean }>`
    padding: 0 16px;
    border-right: ${({ $showBorder }) => ($showBorder ? '1px solid #323232' : 'none')};

    @media (max-width: 768px) {
        border-right: none;
        padding: 0 8px;
        margin-bottom: 4px;
    }
`;

const EligibilityRow = styled(RowBetween)`
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
`;

const EligibilityLeft = styled.div`
    display: flex;
    gap: 20px;

    @media (max-width: 480px) {
        gap: 12px;
    }
`;

const StyledErrorIcon = styled(ErrorIcon)`
    width: 16px;
    height: 16px;
    cursor: pointer;
    color: rgba(88, 88, 88, 1);
`;

const StyledCheckBoxIcon = styled(CheckBoxIcon)<{ $isEligible?: boolean }>`
    width: 18px;
    height: 18px;

    path {
        fill: ${({ $isEligible }) => ($isEligible ? '#5FD15C' : '#999999')};
    }
`;

function BasicInfo() {
    const { todayStats: userLuckyDrawTodayStats, currentRoundStats } = luckyDrawStore;

    const { pointsPerDraw } = currentRoundStats || {};

    const handleNavigateToOnChainTasks = () => {
        window.open(
            'https://oku.trade/swap?inputChain=goat&inToken=0x3022b87ac063DE95b1570F46f5e470F8B53112D8&outToken=0xbC10000000000000000000000000000000000001',
            '_blank',
        );
    };

    const {
        todayMaxDraws,
        totalEarnedPoints,
        todayOnChainTaskEligibility,
        todayEligibility,
        totalConsumedPoints,
        totalRemainingPoints,
        totalAvailableDraws,
        todayConsumedDraws,
    } = userLuckyDrawTodayStats || {};

    return (
        <BasicInfoContainer>
            <TYPE.title fontSize={isMobile ? 20 : 24} fontWeight={700} width="fit-content">
                Basic Info
            </TYPE.title>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    padding: '10px',
                    borderRadius: '12px',
                    backgroundColor: '#F7931A1A',
                    gap: '20px',
                }}
            >
                <TYPE.desc fontSize={14} color="#F7931A" display="flex" alignItems="center" style={{ gap: '8px' }}>
                    <GiftIcon style={{ width: '16px', height: '16px' }} />
                    Lucky Draw Announcement:
                </TYPE.desc>
                <TYPE.desc>Lucky Draw Phase 1 will officially conclude on Dec 27, 10:00 UTC.</TYPE.desc>
                {/* <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                    }}
                >
                    <DetailItem fontSize={14} $showBorder={!isMobile}>
                        Max Draws Per Day = {todayMaxDraws}
                    </DetailItem>
                    <DetailItem fontSize={14} $showBorder={!isMobile}>
                        {getBalanceNumber(new BigNumber(pointsPerDraw || 0)) || '--'} Points = 1 Draw
                    </DetailItem>
                    <DetailItem fontSize={14} $showBorder={false}>
                        Daily Points are counted by UTC time.
                    </DetailItem>
                </div> */}
            </div>
            <InfoCardsContainer>
                <InfoCard>
                    <InfoIcon>
                        <PointsIcon />
                    </InfoIcon>
                    <AutoColumn justify="space-between" gap="4px">
                        <Row gap="10px" align="center">
                            <TYPE.desc fontSize={14} color="#999">
                                Remaining Points
                            </TYPE.desc>
                            <TooltipInfo title="Your currently available points in Season 4. Unused points remain in your balance. Conversion: 200 points = 1 draw.">
                                <StyledErrorIcon />
                            </TooltipInfo>
                        </Row>
                        <TYPE.main fontSize={24}>
                            {totalEarnedPoints
                                ? getFullDisplayBalance(new BigNumber(totalRemainingPoints || 0), 18, 0)
                                : '0'}{' '}
                            Points
                        </TYPE.main>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <TYPE.desc fontSize={14} color="#999">
                                Available Draws:
                            </TYPE.desc>
                            <TYPE.main fontSize={14}>{totalAvailableDraws}</TYPE.main>
                        </div>
                    </AutoColumn>
                </InfoCard>
                <InfoCard>
                    <InfoIcon>
                        <PointsIcon />
                    </InfoIcon>
                    <AutoColumn justify="space-between">
                        <Row gap="10px" align="center">
                            <TYPE.desc fontSize={14} color="#999">
                                Airdrop calculations points
                            </TYPE.desc>
                            <TooltipInfo title="The total points you've spent in Season 4. This figure will be used for future airdrop calculations.">
                                <StyledErrorIcon />
                            </TooltipInfo>
                        </Row>
                        <TYPE.main fontSize={24}>
                            {totalConsumedPoints
                                ? getFullDisplayBalance(new BigNumber(totalConsumedPoints), 18, 0)
                                : '0'}{' '}
                            Points
                        </TYPE.main>
                    </AutoColumn>
                </InfoCard>
                <InfoCard>
                    <InfoIcon>
                        <BoxDoubtIcon />
                    </InfoIcon>
                    <AutoColumn justify="space-between">
                        <Row gap="10px" align="center">
                            <TYPE.desc fontSize={14} color="#999">
                                Today’s Draw Count
                            </TYPE.desc>
                            <TooltipInfo title="Displays the number of draws you have used today.">
                                <StyledErrorIcon />
                            </TooltipInfo>
                        </Row>
                        <TYPE.main fontSize={24}>{todayConsumedDraws}</TYPE.main>
                    </AutoColumn>
                </InfoCard>
            </InfoCardsContainer>
            <div
                style={{
                    width: '100%',
                    height: '1px',
                    backgroundImage:
                        'linear-gradient(90deg, #000000 0%, #6A0DAD 25%, #F7931A 50%, #6A0DAD 75%, #000000 100%)',
                    mask: 'repeating-linear-gradient(90deg, black 0px, black 4px, transparent 4px, transparent 8px)',
                    WebkitMask:
                        'repeating-linear-gradient(90deg, black 0px, black 4px, transparent 4px, transparent 8px)',
                }}
            />
            <AutoColumn gap="20px">
                <EligibilityRow
                    style={{ border: '1px dashed rgba(95, 209, 92, 1)', padding: '20px', borderRadius: '8px' }}
                >
                    <EligibilityLeft>
                        <InfoIcon
                            style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                backgroundColor: 'rgba(50, 50, 50, 1)',
                            }}
                        >
                            <StyledCheckBoxIcon $isEligible={todayEligibility} />
                        </InfoIcon>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
                            <TYPE.desc fontSize={16}>Eligibility Check</TYPE.desc>
                            <TYPE.desc fontSize={16} color="rgba(95, 209, 92, 1)" fontWeight={500}>
                                {Number(todayOnChainTaskEligibility)}/1
                            </TYPE.desc>
                        </div>
                    </EligibilityLeft>
                    <AutoColumn gap="10px">
                        <BindInviteWrapper onClick={handleNavigateToOnChainTasks}>
                            <CheckCircle active={todayEligibility}>
                                <CheckIcon />
                            </CheckCircle>
                            <TYPE.desc fontSize={12}>Daily On-chain Task: Finish $GOATED trade on OKU</TYPE.desc>
                            <TooltipInfo title="Complete today’s GOATED trade on Oku to unlock your Lucky Draw eligibility.">
                                <StyledErrorIcon />
                            </TooltipInfo>
                            <Button
                                style={{
                                    backgroundColor: '#f7931a',
                                    width: '28px',
                                    height: '20px',
                                    borderRadius: '8px',
                                    minWidth: '28px',
                                }}
                            >
                                <ArrowIcon color="#000" width={8} height={8} />
                            </Button>
                        </BindInviteWrapper>
                        <TYPE.desc fontSize={12} color="#fff" textAlign="right">
                            The task check should finish within{' '}
                            <span style={{ color: '#f7931a', fontWeight: 700 }}>30 minutes.</span>
                        </TYPE.desc>
                    </AutoColumn>
                </EligibilityRow>
            </AutoColumn>
        </BasicInfoContainer>
    );
}

export default observer(BasicInfo);
